import { FieldValues, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { ILocationReducer } from 'types/location'
import { NotificationType, NotificationProcessRequiredStepsRules, EnumNotificationProcessRequiredStepsRulesStatus } from 'types/notification'
import { ISectorReducer } from 'types/sector'
import { IVariableReducer } from 'types/variable'
import { stringToDate } from 'utils/dateUtils'
import { getVarible } from 'utils/variblesHandler'
import { filterDamageLevelOptions, requiresSubDamageLevel } from 'utils/damage'

// SELECT
export function variableSelectorOptions(variableSelector: IVariableReducer) {
    if (variableSelector.variablesList) {
        const varible = getVarible(
            variableSelector.variablesList,
            'containerType',
            'container'
        )
        if (varible?.children) {
            const omsChoices = getVarible(
                varible?.children,
                'name',
                'u_oms_classification'
            )
            const reclassifiedIncidentChoices = getVarible(
                varible?.children,
                'name',
                'u_reclassified_incident_type'
            )

            return {
                reclassifiedIncidentTypeSelect:
                    reclassifiedIncidentChoices?.choices
                        ?.slice(1)
                        .map((l) => ({
                            value: l?.value,
                            label: l?.label
                        }))
                        .sort((o1, o2) => o1.label?.localeCompare(o2.label)),
                omsClassifications: omsChoices?.choices
                    ?.slice(1)
                    .map((l) => ({
                        value: l?.value,
                        label: l?.label
                    }))
                    .sort((o1, o2) => o1.label?.localeCompare(o2.label))
            }
        }
    }
}

export function variablesListPacientGender(variableSelector: IVariableReducer) {
    return variableSelector.variablesListPacientGender?.slice(1).map((t) => ({
        id: t.index,
        label: t?.label,
        value: t?.value
    }))
}

export function locationsIcidentList(locationSelector: ILocationReducer) {
    return locationSelector.locationsList
        ?.map((l) => ({
            value: l?.sys_id,
            label: l?.name
        }))
        .sort((o1, o2) => o1.label?.localeCompare(o2.label))
}

export function incidentSectorList(
    sectorSelector: ISectorReducer,
    locationIncident: string
) {
    return sectorSelector.sectorsGroupedByLocationId
        ? sectorSelector.sectorsGroupedByLocationId[locationIncident]
              ?.map((l) => ({
                  value: l?.sys_id,
                  label: l?.u_name
              }))
              .sort((o1, o2) => o1.label?.localeCompare(o2.label))
        : []
}

export function shiftIncidentList(variableSelector: IVariableReducer) {
    return variableSelector.variablesListNotificationTurn
        ?.slice(1)
        .map((t) => ({
            id: t.index,
            label: t?.label,
            value: t?.value
        }))
}

export function variablesListContributingFactor(
    variableSelector: IVariableReducer
) {
    return variableSelector?.variablesListContributingFactor
        ?.slice(1)
        .map((l) => ({
            value: l?.value,
            label: l?.label
        }))
        .sort((o1, o2) => o1.label?.localeCompare(o2.label))
}

export function variablesListDamageLevel(
    variableSelector: IVariableReducer,
    currentDamageLevelValue?: string
) {
    return filterDamageLevelOptions(
        variableSelector.variablesListDamageLevel?.slice(1)?.map((l) => ({
            value: l?.value,
            label: l?.label
        })),
        currentDamageLevelValue
    )
}

export function variablesListPacientDocumentType(variableSelector: IVariableReducer ) {
    const documentType = variableSelector.variablesListPacientDocumentType?.map((t) => ({
        id: t.index,
        label: t?.label,
        value: t?.value
    }))
    return documentType
}

export function setDefaultNotificationValues(
    setValue: UseFormSetValue<FieldValues>,
    notification: NotificationType,
    watch: UseFormWatch<FieldValues>
) {

    setValue('involves_patient', notification?.involves_patient?.value === 'true' )

    const involvesPatient = watch('involves_patient')
    const incidentDate = watch('date_incident') || ''
    const locationIncident = watch('location_incident')
    const patientDate = watch('patient_date_of_birth')

    if (locationIncident) {
        setValue('incident_sector', notification?.incident_sector?.value)
        setValue('reporter_sector', notification?.reporter_sector?.value)
    }

    setValue('u_reclassified_incident_type', notification?.u_reclassified_incident_type?.value)
    setValue('u_oms_classification', notification?.u_oms_classification?.value)
    setValue('incident_title', notification?.incident_title?.value)
    setValue('incident_description', notification?.incident_description?.value)
    setValue('location_incident', notification?.location_incident?.value)
    setValue('shift_incident', notification?.shift_incident?.value)
    setValue('patient_initials', notification?.patient_initials?.value)
    setValue('document_type', notification?.document_type?.value)
    setValue('document_number', notification?.document_number?.value)
    setValue('patient_gender', notification?.patient_gender?.value)
    setValue('u_attachments_size', notification?.u_attachments_size?.value)
    setValue('u_attachments_string', notification?.u_attachments_string?.value)
    setValue('u_image_risk', notification?.u_image_risk?.value)
    setValue('u_number_patients_involved', notification?.u_number_patients_involved?.value || 'um_paciente')
    setValue('u_notification_multiplier', notification?.u_notification_multiplier?.value)
    setValue('immediate_corrective_action', notification?.immediate_corrective_action?.value)
    setValue('contributing_mitigating_factors', notification?.contributing_mitigating_factors?.value)
    setValue('mitigating_factors', notification?.mitigating_factors?.value)
    setValue('u_categorized_contributing_factor', notification?.u_categorized_contributing_factor?.value)
    setValue('u_observation_incident_classification', notification?.u_observation_incident_classification?.value)
    setValue('u_incident_classification', notification?.u_incident_classification?.value)
    setValue('damage_level', notification?.damage_level?.value)
    setValue('date_incident', stringToDate( notification?.date_incident?.display_value, 'dd/MM/yyyy', '/'))   
    setValue('u_never_events_or_sentinel_event', notification?.u_never_events_or_sentinel_event?.value)
    setValue('u_version', notification?.u_version?.value)
    
    if (involvesPatient) {
        setValue('patient_date_of_birth', stringToDate(notification?.patient_date_of_birth?.display_value, 'dd/MM/yyyy', '/')) 
    }
    
    if (patientDate && incidentDate 
        && incidentDate?.toISOString() < patientDate?.toISOString()) {
            setValue('patient_date_of_birth', new Date(incidentDate?.toISOString()))
        }

    if(requiresSubDamageLevel(notification?.damage_level?.value)){
        setValue('u_sub_damage_level', notification?.u_sub_damage_level?.value)
    }

}


export const variableListNumberPatientsInvolved = (variableSelector: IVariableReducer) => {
    const findNumberPatients = variableSelector?.variablesList?.find(
        (item) => item?.label === 'Number patients involved'
    )
    if (findNumberPatients) {
        return findNumberPatients as any
    } else {
        return variableSelector.variablesList
        ?.find((item) => item?.label === 'Sobre o paciente')
        // @ts-ignore
            ?.children?.find(
                (item) => item?.label === 'Number patients involved'
            )
    }
}
